import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { useVisuallyHidden } from '@uhg-abyss/web/hooks/useVisuallyHidden';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Text } from '@uhg-abyss/web/ui/Text';
import find from 'lodash/find';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import translationsEnJson from '../../../public/locale/transperfect/en/translation.json';
import { SearchFilterContext } from '../../context/SearchFilterContext';
import { useCoverageType } from '../../hooks/useCoverageType';
import { useCurrentMemberIsPreEffective } from '../../hooks/useCurrentMemberIsPreEffective';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useLagoon } from '../../hooks/useLagoon';
import { useSmartChoice } from '../../hooks/useSmartChoice';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import { getCurrentMember } from '../../utils/user.utils';
import { adobeSearchFilterEvent } from '../AdobeTagging/adobeSearchFilterEvent';
import {
  Constants,
  ReverseCoverageTypesCodes,
  SortOptionValues,
} from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { cutOffSortDropdown, mobileOnly, phoneOnly } from '../ConstantsStyles';
import {
  getFormattedFilters,
  getSearchTerm,
} from '../Utils/adobeTrackUtils/adobeTrackUtils';
import { InfoModal } from './InfoModal';
import { showUpcomingAvailabilityOption } from './ResultSortUtils';
import { SortBy } from './SortBy';
import { SortByMobile } from './SortByMobile';
import {
  getBestMatchOptionKey,
  getRecommendedProviderOptionKey,
} from './SortByUtil';

export const SortContainer = styled('div', {
  '.abyss-radio-root ~div': {
    '&:hover': {
      backgroundColor: '$core.color.aqua.15',
    },
  },
});

export const ToggleSortButton = styled('div', {
  dynamic: ({
    cssProps: { enableMapViewEnhancements, enableMapViewHideOption } = {},
  }) => ({
    backgroundColor: enableMapViewEnhancements ? 'white' : null,
    padding: enableMapViewHideOption ? '$sm $sm $sm $md' : '$sm $lg $sm $md',
    border: enableMapViewHideOption
      ? '1px solid $core.color.brand.60'
      : '1px solid $core.color.neutral.30',
    borderRadius: enableMapViewHideOption ? '8px' : '24px',
  }),
  height: '42px',
  fontSize: '16px',
  padding: '$sm $lg $sm $md',
  border: '1px solid $core.color.neutral.30',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  gap: '$xs',
});

type Props = {
  sectionType?: string;
  isMapView?: boolean;
  hideOverflow?: boolean;
  expandedProviderListView?: boolean;
  enableMapViewHideOption?: boolean;
};

const { PROVIDER, FACILITY } = Constants.RESULT_SECTION;

const filterSortLabel = (searchFilters, t, sectionType) => {
  const sortValue = searchFilters?.SortBy?.value;
  const sortLabel = searchFilters?.SortBy?.label;

  if (!sortValue) {
    return t('SORT_BUTTONS.SORT');
  }

  if (sortLabel === t('SORT_OPTIONS.BH_PREFERRED_PROVIDERS')) {
    return t('SORT_OPTIONS.BH_PREFERRED_PROVIDERS');
  }

  const isFacilitySection = sectionType?.toLowerCase() === 'facility';
  const isBestMatchLikeValue = sortValue === Constants.PROVIDER_SEARCH_SORT_BY;

  if (isFacilitySection && isBestMatchLikeValue) {
    return t('SORT_OPTIONS.PREFERRED_FACILITIES');
  }

  return t(`SORT_OPTIONS.${sortValue}`);
};

const findBestMatchContent = (data, key) => find(data, { key });
const handleVisionBestMatchContent = (sectionType, data) => {
  const keys = {
    [FACILITY]:
      ConstantsLagoon.CONTENT_FILTERS
        .BEST_MATCH_POPOVER_CONTENT_VISION_FACILITY,
    [PROVIDER]:
      ConstantsLagoon.CONTENT_FILTERS
        .BEST_MATCH_POPOVER_CONTENT_VISION_PROVIDER,
  };
  return findBestMatchContent(data, keys[sectionType]);
};
const handleMedicalBestMatchContent = (sectionType, data) => {
  const keys = {
    [FACILITY]: ConstantsLagoon.CONTENT_FILTERS.BEST_MATCH_MEDICAL_FACILITY,
    [Constants.RESULT_SECTION.PROVIDER_GROUPS]:
      ConstantsLagoon.CONTENT_FILTERS.BEST_MATCH_MEDICAL_PROVIDER_GROUP,
    [PROVIDER]: ConstantsLagoon.CONTENT_FILTERS.BEST_MATCH_MEDICAL_PROVIDER,
  };
  return findBestMatchContent(data, keys[sectionType]);
};
const handleBehavioralHealthBestMatchContent = (sectionType, data) => {
  const keys = {
    [FACILITY]: ConstantsLagoon.CONTENT_FILTERS.BEST_MATCH_BH_FACILITY,
    [Constants.RESULT_SECTION.PROVIDER_GROUPS]:
      ConstantsLagoon.CONTENT_FILTERS.BEST_MATCH_BH_PROVIDER_GROUP,
    [PROVIDER]: ConstantsLagoon.CONTENT_FILTERS.BEST_MATCH_BH_PROVIDER,
  };
  return findBestMatchContent(data, keys[sectionType]);
};

const bestMatchContent = (coverageType, sectionType, data, isCns) => {
  if (coverageType === ReverseCoverageTypesCodes.VISION) {
    return handleVisionBestMatchContent(sectionType, data);
  }

  if (coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH']) {
    return handleBehavioralHealthBestMatchContent(sectionType, data);
  }

  if (isCns) {
    switch (coverageType) {
      case ReverseCoverageTypesCodes.MEDICAL:
        return handleMedicalBestMatchContent(sectionType, data);
      case ReverseCoverageTypesCodes['BEHAVIORAL HEALTH']:
        return handleBehavioralHealthBestMatchContent(sectionType, data);
      case ReverseCoverageTypesCodes.DENTAL:
        if (sectionType === PROVIDER) {
          return find(data, {
            key: ConstantsLagoon.CONTENT_FILTERS.BEST_MATCH_DENTAL_PROVIDER,
          });
        }
        break;
    }
  }
  return findBestMatchContent(
    data,
    ConstantsLagoon.CONTENT_FILTERS.BEST_MATCH_POPOVER_CONTENT
  );
};

export const ResultSort = ({
  sectionType,
  isMapView,
  hideOverflow,
  expandedProviderListView,
  enableMapViewHideOption = false,
}: Props) => {
  const { t } = useTranslation();
  const typeaheadState = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      search: state.typeaheadSearchStore.search,
      searchMethod: state.typeaheadSearchStore.searchMethod,
      searchTerm: state.typeaheadSearchStore.searchTerm,
    }))
  );

  const featureInclusion = useLagoon('feature-inclusion')();
  const currentMember = getCurrentMember();
  const isCns = currentMember?.isCnSMember;

  const [
    enableMapViewEnhancements,
    enableAlphaSortOption,
    showCustomSortFlag,
    showCustomSortEarlyPayFlag,
    showUpcomingAvailability,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LIST_VIEW_MAP_ENHANCEMENTS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_ALPHA_SORT_OPTION,
    ConstantsLagoon.FEATURE_FLAGS.RESULT_PAGE_CUSTOM_SORT,
    ConstantsLagoon.FEATURE_FLAGS.RESULT_PAGE_CUSTOM_SORT_EARLYPAY,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SORT_UPCOMING_AVAILABILITY,
  ]);

  const showCustomSort = currentMember?.isIFPEarlyPay
    ? showCustomSortEarlyPayFlag
    : showCustomSortFlag;

  const upcomingAvailabilityEligible = isCns
    ? featureInclusion
        ?.find((row) => row?.dntFeature === 'DocASAP')
        ?.policyIds.includes(currentMember?.policyNumber)
    : true;

  const isPreEffective = useCurrentMemberIsPreEffective();

  const data = useLagoon('ui-messaging')();

  const smartChoiceEligibility = useSmartChoice();
  const isSmartChoice = smartChoiceEligibility.isEnabled;

  const coverageType = useCoverageType();
  const content = bestMatchContent(
    coverageType,
    sectionType,
    data,
    isCns
  )?.message;

  const BEST_MATCH_SORT_LABEL_KEY = getBestMatchOptionKey(
    coverageType,
    sectionType?.toLowerCase() ?? ''
  );
  const SMART_CHOICE = getRecommendedProviderOptionKey(coverageType);
  const optionKey = isSmartChoice
    ? { RECOMMENDED_PROVIDERS: t(`SORT_OPTIONS.${SMART_CHOICE}`) }
    : { BEST_MATCH: t(`SORT_OPTIONS.${BEST_MATCH_SORT_LABEL_KEY}`) };
  const options = {
    ...optionKey,
    CLOSEST: t('SORT_OPTIONS.CLOSEST'),
    HIGHEST_RATING: t('SORT_OPTIONS.HIGHEST_RATING'),
    FIRST_NAME_A_Z: t('SORT_OPTIONS.FIRST_NAME_A_Z'),
    FIRST_NAME_Z_A: t('SORT_OPTIONS.FIRST_NAME_Z_A'),
  };
  const customSort = {
    UPCOMING_AVAILABILITY: t('SORT_OPTIONS.UPCOMING_AVAILABILITY'),
    TIER_PROV: t('SORT_OPTIONS.TIER_PROV'),
    ACO_PROV: t('SORT_OPTIONS.ACO_PROV'),
    UHPD_PROV: t('SORT_OPTIONS.UHPD_PROV'),
    COST_EFF_RATING: t('SORT_OPTIONS.COST_EFF_RATING'),
  };

  const { searchFilters, setSearchFilters } = useContext(SearchFilterContext);
  const [isOpen, setIsOpen] = useState(false);
  const mobileScreen = useMediaQuery(
    enableMapViewEnhancements ? mobileOnly : phoneOnly
  );
  const { visuallyHiddenProps } = useVisuallyHidden();
  const onApply = (value: string) => {
    const selectedFilter = {
      SortBy: {
        label: options[value] || customSort[value],
        value,
        hide: true,
        dnt_label: translationsEnJson.SORT_OPTIONS[value],
      },
    };
    setSearchFilters({
      ...searchFilters,
      ...selectedFilter,
    });

    adobeSearchFilterEvent({
      type: sectionType,
      term: getSearchTerm(typeaheadState),
      filters: getFormattedFilters(selectedFilter),
    });
  };
  const sort = {
    label: (
      <React.Fragment>
        <ToggleSortButton
          cssProps={{ enableMapViewEnhancements, enableMapViewHideOption }}
          data-auto-testid="toggle-sort-button"
          data-testid="toggle-sort-button"
        >
          {!enableMapViewHideOption ? (
            <IconSymbol
              color={
                mobileScreen ? '$core.color.brand.100' : '$descriptionBlack'
              }
              icon={isMapView ? 'sort' : 'swap_vert'}
            />
          ) : null}
          <Text
            color={enableMapViewHideOption ? '$core.color.brand.60' : undefined}
            css={
              enableMapViewHideOption
                ? {
                    'abyss-text-root': {
                      width: 'max-content',
                      ...cutOffSortDropdown(hideOverflow),
                      lineHeight: '25px !important',
                    },
                  }
                : {
                    'abyss-text-root': {
                      lineHeight: '25px !important',
                    },
                  }
            }
            fontSize="md"
            fontWeight="medium"
          >
            {filterSortLabel(searchFilters, t, sectionType)}
          </Text>
          <IconSymbol
            color={
              expandedProviderListView
                ? '$core.color.brand.60'
                : mobileScreen
                ? '$core.color.brand.100'
                : '$descriptionBlack'
            }
            icon="keyboard_arrow_down"
          />
        </ToggleSortButton>
      </React.Fragment>
    ),
    title: t('SORT_BUTTONS.SORT_RESULT'),
    name: t('SORT_BUTTONS.SORT'),
    icon: 'sort',
    model: 'SortBy',
    type: 'radio',
    options: [
      {
        label: (
          <React.Fragment>
            <Flex css={{ gap: '$xs' }}>
              {isSmartChoice ? (
                <Text>{options?.RECOMMENDED_PROVIDERS}</Text>
              ) : (
                <>
                  <Text>{options?.BEST_MATCH}</Text>
                  <span {...visuallyHiddenProps}>
                    {t('SORT_OPTIONS.BEST_MATCH_POPOVER_CONTENT')}
                  </span>
                </>
              )}
            </Flex>
          </React.Fragment>
        ),
        value: isSmartChoice
          ? Constants.RECOMMENDED_PROVIDERS_SEARCH_SORT_BY
          : Constants.PROVIDER_SEARCH_SORT_BY,
        dnt_label: isSmartChoice
          ? translationsEnJson.SORT_OPTIONS[SMART_CHOICE]
          : translationsEnJson.SORT_OPTIONS[BEST_MATCH_SORT_LABEL_KEY],
      },
      ...(coverageType === ReverseCoverageTypesCodes.VISION &&
      sectionType === PROVIDER
        ? [
            {
              label: options.CLOSEST,
              value: SortOptionValues.CLOSEST,
              dnt_label: translationsEnJson.SORT_OPTIONS.CLOSEST,
            },
            {
              label: options.HIGHEST_RATING,
              value: SortOptionValues.HIGHEST_RATING,
              dnt_label: translationsEnJson.SORT_OPTIONS.HIGHEST_RATING,
            },
          ]
        : [
            ...(sectionType === Constants.RESULT_SECTION.PROVIDER
              ? [
                  {
                    label: options.HIGHEST_RATING,
                    value: SortOptionValues.HIGHEST_RATING,
                    dnt_label: translationsEnJson.SORT_OPTIONS.HIGHEST_RATING,
                  },
                ]
              : []),
            {
              label: options.CLOSEST,
              value: SortOptionValues.CLOSEST,
              dnt_label: translationsEnJson.SORT_OPTIONS.CLOSEST,
            },
          ]),
      ...(showUpcomingAvailabilityOption(
        showUpcomingAvailability,
        isPreEffective,
        upcomingAvailabilityEligible
      )
        ? [
            ...(sectionType === PROVIDER &&
            (coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] ||
              coverageType === ReverseCoverageTypesCodes.MEDICAL)
              ? [
                  {
                    label: customSort.UPCOMING_AVAILABILITY,
                    value: SortOptionValues.UPCOMING_AVAILABILITY,
                    dnt_label:
                      translationsEnJson.SORT_OPTIONS.UPCOMING_AVAILABILITY,
                  },
                ]
              : []),
          ]
        : []),
      ...(showCustomSort
        ? [
            {
              label: customSort.TIER_PROV,
              value: SortOptionValues.TIER_PROV,
              dnt_label: translationsEnJson.SORT_OPTIONS.TIER_PROV,
            },
            {
              label: customSort.ACO_PROV,
              value: SortOptionValues.ACO_PROV,
              dnt_label: translationsEnJson.SORT_OPTIONS.ACO_PROV,
            },
            {
              label: customSort.UHPD_PROV,
              value: SortOptionValues.UHPD_PROV,
              dnt_label: translationsEnJson.SORT_OPTIONS.UHPD_PROV,
            },
            {
              label: customSort.COST_EFF_RATING,
              value: SortOptionValues.COST_EFF_RATING,
              dnt_label: translationsEnJson.SORT_OPTIONS.COST_EFF_RATING,
            },
          ]
        : []),
      ...(enableAlphaSortOption
        ? sectionType !== Constants.RESULT_SECTION.PROVIDER
          ? [
              {
                label: options.FIRST_NAME_A_Z,
                value: SortOptionValues.ALPHA_A_Z,
                dnt_label: translationsEnJson.SORT_OPTIONS.ALPHA_A_Z,
              },
              {
                label: options.FIRST_NAME_Z_A,
                value: SortOptionValues.ALPHA_Z_A,
                dnt_label: translationsEnJson.SORT_OPTIONS.ALPHA_Z_A,
              },
            ]
          : [
              {
                label: options.FIRST_NAME_A_Z,
                value: SortOptionValues.ALPHA_A_Z_FIRSTNAME,
                dnt_label: translationsEnJson.SORT_OPTIONS.FIRST_NAME_A_Z,
              },
              {
                label: options.FIRST_NAME_Z_A,
                value: SortOptionValues.ALPHA_Z_A_FIRSTNAME,
                dnt_label: translationsEnJson.SORT_OPTIONS.FIRST_NAME_Z_A,
              },
            ]
        : []),
    ],
  };

  return (
    <SortContainer>
      {mobileScreen ? (
        <SortByMobile
          bestMatchTitle={options.BEST_MATCH}
          content={content}
          defaultValue={searchFilters?.SortBy?.value}
          icon={sort.icon}
          model={sort.model}
          name={sort.name}
          onApply={onApply}
          options={sort.options}
          size="$sm"
          smartChoiceTitle={options?.RECOMMENDED_PROVIDERS}
          title={sort.title}
        />
      ) : (
        <SortBy
          bestMatchTitle={options.BEST_MATCH}
          content={content}
          defaultValue={searchFilters?.SortBy?.value}
          label={sort.label}
          model={sort.model}
          onApply={onApply}
          options={sort.options}
          smartChoiceTitle={options?.RECOMMENDED_PROVIDERS}
          title={sort.title}
        />
      )}
      <InfoModal
        dataForAnalytics={{
          linkName: sort.title,
          linkLocation: `body:sort by`,
          modalName: sort.title,
        }}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
    </SortContainer>
  );
};
